import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import Spinner from "./Spinner";

const Pagination = ({
  onPageClick,
  total,
  limit,
  page,
  isFetching,
  changeLimit = false,
  onLimitChange,
  alwayShowPagination = true,
  isLoading,
  enableManualMode = false,
  autoLoadThreshold = 3,
  infiniteScroll = false,
  readyForPagination = true,
  scrollContainerRefCurrent = null,
  showEndOfList = false,
  endOfListText = "End of list",
}) => {
  const safeLimit = Math.max(1, limit || 1);
  const maxPage = useMemo(
    () => Math.max(1, Math.ceil(total / safeLimit)),
    [total, safeLimit]
  );
  const totalPages = Math.ceil(total / safeLimit);
  const hasMore = page < totalPages;

  const [manualLoadMode, setManualLoadMode] = useState(false);
  const [scrollLoadCount, setScrollLoadCount] = useState(0);

  const loadMoreRef = useRef(null);
  const prevPageRef = useRef(page);

  const getPaginationRange = useCallback((current, totalPages, delta = 1) => {
    const range = [];
    const alwaysShowFirst = 1;
    const alwaysShowLast = totalPages;
    const start = Math.max(2, current - delta);
    const end = Math.min(totalPages - 1, current + delta);

    range.push(alwaysShowFirst);
    if (start > 2) range.push("...");
    for (let i = start; i <= end; i++) range.push(i);
    if (end < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(alwaysShowLast);
    return range;
  }, []);

  const showMore = () => {
    if (!isLoading && !isFetching && hasMore) {
      handlePageClick(page + 1);
    }
    setScrollLoadCount(0);
    setManualLoadMode(false);
  };

  const handlePageClick = useCallback(
    (nextPage) => {
      if (typeof onPageClick !== "function") return;
      if (!isFetching && !isLoading) onPageClick(nextPage);
    },
    [isFetching, isLoading, onPageClick]
  );

  // Intersection Observer for auto load
  useEffect(() => {
    if (!infiniteScroll) return;
    if (!readyForPagination) return;
    if (manualLoadMode || isLoading || isFetching || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && !isFetching && hasMore) {
          handlePageClick(page + 1);
        }
      },
      {
        root: scrollContainerRefCurrent || null,
        rootMargin: "100px",
        threshold: 0,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [
    infiniteScroll,
    manualLoadMode,
    isLoading,
    isFetching,
    hasMore,
    page,
    handlePageClick,
    scrollContainerRefCurrent,
  ]);

  // Track auto-load count
  useEffect(() => {
    if (!infiniteScroll || !enableManualMode) return;
    if (!readyForPagination) return;

    if (!isLoading && page > prevPageRef.current) {
      setScrollLoadCount((prev) => {
        const next = prev + 1;
        if (next >= autoLoadThreshold) {
          setManualLoadMode(true);
        }
        return next;
      });
      prevPageRef.current = page;
    }
  }, [infiniteScroll, page, isLoading, enableManualMode, autoLoadThreshold]);

  useEffect(() => {
    setManualLoadMode(false);
    setScrollLoadCount(0);
    prevPageRef.current = page;
  }, [total]);

  // Early returns
  if (
    (!infiniteScroll && !alwayShowPagination && total <= safeLimit) ||
    (infiniteScroll && total === 0)
  ) {
    return null;
  }

  return !infiniteScroll ? (
    <div className="pagination">
      <div className="pagination-controls numbered">
        <button
          onClick={() => handlePageClick(Math.max(page - 3, 1))}
          disabled={page <= 3 || isFetching || isLoading}
        >
          &laquo;
        </button>
        <button
          onClick={() => handlePageClick(Math.max(page - 1, 1))}
          disabled={page <= 1 || isFetching || isLoading}
        >
          &lsaquo;
        </button>

        {getPaginationRange(page, maxPage).map((toPage, index) =>
          toPage === "..." ? (
            <span
              key={`ellipsis-${index}-${page}`}
              className="pagination-ellipsis"
            >
              …
            </span>
          ) : (
            <button
              key={toPage}
              onClick={() => handlePageClick(toPage)}
              className={page === toPage ? "active" : ""}
              disabled={isFetching || isLoading}
            >
              {toPage}
            </button>
          )
        )}

        <button
          onClick={() => handlePageClick(Math.min(page + 1, maxPage))}
          disabled={page >= maxPage || isFetching || isLoading}
        >
          &rsaquo;
        </button>
        <button
          onClick={() => handlePageClick(Math.min(page + 3, maxPage))}
          disabled={page >= maxPage - 2 || isFetching || isLoading}
        >
          &raquo;
        </button>
      </div>

      {changeLimit && (
        <div className="pagination-limit">
          <label htmlFor="pagination-limit">Rows per page:</label>
          <select
            id="pagination-limit"
            value={limit}
            onChange={(e) => onLimitChange?.(Number(e.target.value))}
            disabled={isFetching || isLoading}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  ) : (
    <>
      {enableManualMode && manualLoadMode && !isLoading && hasMore && (
        <button
          onClick={showMore}
          className="viewmore"
          aria-label="Load more items"
        >
          View More
        </button>
      )}

      <div
        ref={
          !manualLoadMode && hasMore && readyForPagination ? loadMoreRef : null
        }
        style={{ textAlign: "center", padding: "1rem" }}
      >
        {(isLoading || isFetching) && <Spinner type="small" />}

        {!hasMore && !(isLoading || isFetching) && showEndOfList && (
          <span>{endOfListText}</span>
        )}
      </div>
    </>
  );
};

export default Pagination;
